import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { TbMessageChatbot } from 'react-icons/tb';
import { IoClose } from 'react-icons/io5';
import { FiSend } from 'react-icons/fi';

import { useTheme } from '../contexts/ThemeProvider';
import { profile } from '../data/profile';
import { DAILY_LIMIT, MAX_QUESTION_LENGTH, askAssistant, getRemaining } from '../utils/assistant';

const greeting = {
	from: 'bot',
	text: `Hi! I'm ${profile.shortName}'s assistant. Ask me about ${profile.shortName}'s work, projects, or skills.`,
};

const suggestions = [
	`What does ${profile.shortName} do at GoRated?`,
	`What has ${profile.shortName} built with LLMs?`,
	`What's ${profile.shortName}'s tech stack?`,
];

const ChatWidget = () => {
	const color = useTheme();
	const [isOpen, setIsOpen] = useState(false);
	const [messages, setMessages] = useState([greeting]);
	const [input, setInput] = useState('');
	const [isPending, setIsPending] = useState(false);
	// null until the backend says how many are left
	const [remaining, setRemaining] = useState(null);
	const messagesRef = useRef(null);
	const inputRef = useRef(null);

	useEffect(() => {
		if (messagesRef.current) messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
	}, [messages, isPending]);

	useEffect(() => {
		if (!isOpen) return;
		inputRef.current?.focus();
		getRemaining()
			.then(setRemaining)
			.catch(() => setRemaining(null));
	}, [isOpen]);

	const outOfQuestions = remaining === 0;

	const ask = async (question) => {
		const text = question.trim();
		if (!text || isPending || outOfQuestions) return;

		// Earlier turns, so a follow-up like "what stack did it use?" makes sense
		const history = messages.filter((message) => message !== greeting);
		setMessages((prev) => [...prev, { from: 'user', text }]);
		setInput('');
		setIsPending(true);

		try {
			const reply = await askAssistant(text, history);
			setMessages((prev) => [...prev, { from: 'bot', text: reply.answer }]);
			setRemaining(reply.remaining);
		} catch (error) {
			setMessages((prev) => [...prev, { from: 'bot', text: error.message }]);
			if (typeof error.remaining === 'number') setRemaining(error.remaining);
		} finally {
			setIsPending(false);
			inputRef.current?.focus();
		}
	};

	const submitHandler = (e) => {
		e.preventDefault();
		ask(input);
	};

	const hasAsked = messages.some((message) => message.from === 'user');

	return (
		<Container color={color.color}>
			{isOpen && (
				<div className="panel" role="dialog" aria-label={`Ask about ${profile.shortName}`}>
					<div className="panel-header">
						<div>
							<h3>ask about {profile.shortName.toLowerCase()}_</h3>
							<p>
								{remaining === null ? `${DAILY_LIMIT} questions a day` : `${remaining} of ${DAILY_LIMIT} questions left today`}
							</p>
						</div>
						<button className="icon-button" onClick={() => setIsOpen(false)} aria-label="Close chat">
							<IoClose />
						</button>
					</div>

					<div className="messages" ref={messagesRef}>
						{messages.map((message, index) => (
							<div className={`message ${message.from}`} key={index}>
								{message.text}
							</div>
						))}
						{isPending && <div className="message bot pending">thinking...</div>}

						{!hasAsked && !outOfQuestions && (
							<div className="suggestions">
								{suggestions.map((suggestion) => (
									<button key={suggestion} onClick={() => ask(suggestion)}>
										{suggestion}
									</button>
								))}
							</div>
						)}
					</div>

					<form className="composer" onSubmit={submitHandler}>
						<input
							ref={inputRef}
							value={input}
							onChange={(e) => setInput(e.target.value)}
							maxLength={MAX_QUESTION_LENGTH}
							disabled={outOfQuestions}
							placeholder={outOfQuestions ? 'Come back tomorrow!' : 'Ask a question...'}
							aria-label="Your question"
						/>
						<button type="submit" className="icon-button" disabled={!input.trim() || isPending || outOfQuestions} aria-label="Send">
							<FiSend />
						</button>
					</form>
				</div>
			)}

			<button className="launcher" onClick={() => setIsOpen((open) => !open)} aria-label={isOpen ? 'Close chat' : `Ask about ${profile.shortName}`}>
				{isOpen ? <IoClose /> : <TbMessageChatbot />}
			</button>
		</Container>
	);
};

export default ChatWidget;

const Container = styled.div`
	font-family: 'SHPinscher', sans-serif;
	color: ${({ color }) => color.text};

	button {
		font-family: inherit;
		cursor: pointer;
	}

	.launcher {
		position: fixed;
		left: 1rem;
		bottom: 1rem;
		z-index: 1000;
		width: 3.75rem;
		height: 3.75rem;
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 1.9rem;
		border-radius: 50%;
		border: 2px solid ${({ color }) => color.primary};
		background-color: ${({ color }) => color.background};
		color: ${({ color }) => color.text};
		transition: 0.1s ease-in;

		&:hover,
		&:active {
			transform: translateY(-3px);
			box-shadow: 8px 2px 40px ${({ color }) => color.textShadow};
			color: ${({ color }) => color.background};
			background-color: ${({ color }) => color.primary};
		}
	}

	.panel {
		position: fixed;
		left: 1rem;
		bottom: 5.5rem;
		z-index: 1000;
		width: 24rem;
		height: 32rem;
		max-height: calc(100vh - 7rem);
		display: flex;
		flex-direction: column;
		border: 2px solid ${({ color }) => color.primary};
		border-radius: 12px;
		background-color: ${({ color }) => color.background};
		box-shadow: 8px 2px 60px ${({ color }) => color.textShadow};
		overflow: hidden;
	}

	.panel-header {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		padding: 1rem 1.25rem;
		border-bottom: 2px solid ${({ color }) => color.navBorder};

		h3 {
			font-size: 1.5rem;
			letter-spacing: 1.5px;
		}
		p {
			font-size: 1rem;
			letter-spacing: 1px;
			color: ${({ color }) => color.label};
		}
	}

	.icon-button {
		display: flex;
		align-items: center;
		justify-content: center;
		font-size: 1.4rem;
		padding: 0.35rem;
		border: none;
		border-radius: 3px;
		background: none;
		color: ${({ color }) => color.text};

		&:disabled {
			color: ${({ color }) => color.disabled};
			cursor: default;
		}
	}

	.messages {
		flex: 1;
		overflow-y: auto;
		padding: 1rem 1.25rem;
		display: flex;
		flex-direction: column;
		gap: 0.75rem;

		.message {
			max-width: 85%;
			padding: 0.6rem 0.9rem;
			border-radius: 12px;
			font-size: 1.1rem;
			line-height: 1.4;
			letter-spacing: 1px;
			white-space: pre-wrap;
			overflow-wrap: anywhere;
		}
		.bot {
			align-self: flex-start;
			border: 2px solid ${({ color }) => color.primary};
			border-bottom-left-radius: 3px;
		}
		.user {
			align-self: flex-end;
			background-color: ${({ color }) => color.primary};
			color: ${({ color }) => color.background};
			border-bottom-right-radius: 3px;
		}
		.pending {
			color: ${({ color }) => color.label};
		}

		.suggestions {
			display: flex;
			flex-direction: column;
			align-items: flex-start;
			gap: 0.5rem;
			button {
				text-align: left;
				font-size: 1rem;
				letter-spacing: 1px;
				padding: 0.4rem 0.8rem;
				border-radius: 3px;
				border: 2px solid ${({ color }) => color.navBorder};
				background-color: ${({ color }) => color.background};
				color: ${({ color }) => color.text};
				transition: 0.1s ease-in;
				&:hover {
					border-color: ${({ color }) => color.primary};
					border-radius: 12px;
				}
			}
		}
	}

	.composer {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.75rem 1rem;
		border-top: 2px solid ${({ color }) => color.navBorder};

		input {
			flex: 1;
			min-width: 0;
			font-family: inherit;
			font-size: 1.1rem;
			letter-spacing: 1px;
			padding: 0.6rem 0.9rem;
			border-radius: 3px;
			border: 2px solid ${({ color }) => color.primary};
			background-color: ${({ color }) => color.background};
			color: ${({ color }) => color.text};
			transition: 0.1s ease-in;
			&:focus {
				outline: none;
				border-radius: 12px;
			}
			&:disabled {
				border-color: ${({ color }) => color.disabled};
			}
		}
	}

	@media (max-width: 768px) {
		.panel {
			right: 1rem;
			left: 1rem;
			width: auto;
			height: 70vh;
		}
	}
`;
